<script lang="ts" setup>
import AGrowthHack from '~/components/reusable/AGrowthHack.vue'
import HolderMap from '~/components/reusable/HolderMap.vue'
import YourPrivateEvent from '~/components/reusable/YourPrivateEvent.vue'

import IconNotDone from '~/assets/images/creator/close.svg?component'
import IconDone from '~/assets/images/creator/done.svg?component'

const { t } = useI18n()

const {
  public: { siteUrl },
} = useRuntimeConfig()

useSeoMeta({
  title: t('belong-pass-for-creators'),
  description: t('launch-your-token-gated'),
  ogTitle: t('belong-pass-for-creators'),
  ogDescription: t('launch-your-token-gated'),
  ogImage: `${siteUrl}/images/head/snippet_screen.png`,
  ogImageWidth: '1200',
  ogImageHeight: '630',
  ogImageType: 'image/png',
  ogType: 'website',
  twitterTitle: t('belong-pass-for-creators'),
  twitterDescription: t('launch-your-token-gated'),
  twitterImage: `${siteUrl}/images/head/snippet_screen.png`,
  twitterCard: 'summary_large_image',
})

definePageMeta({
  layout: 'menu',
})

const features = [
  { title: '' },
  {
    titleKey: 'mobile-app',
    icon: 'mobile-app',
    class: 'w-3 h-5.5',
  },
  {
    titleKey: 'automated-mint-page-generator',
    icon: 'automated-mint-page-generator',
    class: 'w-5.5 h-5',
  },
  {
    titleKey: 'soulbound-nfts',
    icon: 'soulbound-nfts',
    class: 'w-5.5 h-5.25',
  },
  {
    titleKey: 'royalties-support',
    icon: 'royalties-support',
    class: 'w-5.75 h-5',
  },
  {
    titleKey: 'pre-audited-smart',
    icon: 'pre-audited-smart-contracts',
    class: 'w-3.5 h-5.75',
  },
  {
    titleKey: 'nft-ticketing-qr-scanner',
    icon: 'nft-ticketing-qr-scanner',
    class: 'w-5 h-5',
  },
  {
    titleKey: 'allowlist-management',
    icon: 'allowlist-management',
    class: 'w-4 h-5.25',
  },
  {
    titleKey: 'push-notifications',
    icon: 'push-notifications',
    class: 'w-4.5 h-5',
  },
  {
    titleKey: 'multichain-support',
    icon: 'multichain-support',
    class: 'w-4.75 h-5',
  },
  {
    titleKey: 'support-for-expiring-nft-memberships',
    icon: 'support-for-expiring-nft-memberships',
    class: 'w-4.75 h-4.25',
  },
  {
    titleKey: 'mint-with-credit-card',
    icon: 'mint-with-credit-card',
    class: 'w-5.5 h-3.75',
  },
  {
    titleKey: 'cost',
  },
]

const headers = [
  { alt: 'Belong', src: '/images/creator/logo.png', w: 35, h: 10.75 },
  {
    alt: 'Manifold',
    src: '/images/creator/manifold.png',
  },
  {
    alt: 'NiftyKit',
    src: '/images/creator/niftyKit.png',
  },
  {
    alt: 'Web3Mint',
    src: '/images/creator/web3mint.png',
  },
  { alt: 'Bueno', src: '/images/creator/bueno.png', w: 28, h: 7.75 },
]

const rows = [
  ['done', 'close', 'close', 'close', 'close'],
  ['done', 'done', 'close', 'close', 'done'],
  ['done', 'close', 'close', 'close', 'close'],
  ['done', 'close', 'done', 'close', 'done'],
  ['done', 'close', 'done', 'close', 'done'],
  ['done', 'close', 'close', 'close', 'close'],
  ['done', 'done', 'done', 'done', 'done'],
  ['done', 'close', 'close', 'close', 'close'],
  ['done', 'close', 'done', 'close', 'done'],
  ['done', 'close', 'close', 'close', 'close'],
  ['done', 'done', 'close', 'done', 'close'],
]

const summaryRow = [
  {
    key: 'with-belong-creator-pass',
  },
  { key: 'eth-per-transaction' },
  { key: 'eth-of-sales' },
  { key: 'of-sales-year' },
  { key: 'of-sales' },
]
</script>

<template>
  <div class="text-white">
    <section class="relative">
      <div class="h-174 max-xl:h-auto">
        <div class="ml-26 max-xl:mx-auto">
          <h1 class="pt-51 text-[#CB99FF] max-xl:pt-159 max-xl:text-center">
            {{ t('belong-creator-pass') }}
          </h1>
          <h1
            class="text-[1.75rem] font-normal tracking-wider mt-2.5 leading-[2.125rem] max-w-132.25 mb-0 max-xl:text-center max-xl:mx-auto max-md:px-5.5 max-md:text-center"
          >
            {{ t('launch-your-token-gated') }}
          </h1>
          <div
            class="flex flex-row mt-20.5 items-start max-xl:items-center max-xl:flex-col max-xl:mt-7"
          >
            <div class="supply-block flex flex-col max-xl:flex-row">
              <div
                class="text-[0.8125rem] tracking-wide leading-[1.25rem] z-2 relative rounded-[1.875rem] bg-[#1B1B1B] w-61.75 h-8.75 flex items-center justify-center max-xl:mx-auto"
              >
                {{ t('supply') }}
                <span class="text-[#CB99FF] ml-2.5">{{
                  t('open-mint-for-7-days')
                }}</span>
              </div>

              <div class="price-text">
                <span>{{ t('price') }} </span>
                <span class="text-[#72FFCC]">{{ t('tba') }}</span>
              </div>
            </div>

            <NuxtLink
              to="/"
              class="mt-2 text-[0.8125rem] font-light leading-[1.25rem] tracking-wide flex items-center ml-6 underline text-white max-xl:justify-center max-xl:z-2 max-xl:mt-5 max-xl:mb-5 max-xl:mx-auto"
            >
              {{ t('how-can-i-get-one') }}
            </NuxtLink>
          </div>
          <div class="gap-3.5 mt-8 hidden xl:flex">
            <ReusableInstallButtons />
          </div>
        </div>
        <div
          class="header-shadow w-full object-cover absolute top-0 -z-1 h-175"
        >
          <div
            class="video-desktop w-full object-cover absolute top-0 -z-1 max-xl:h-175 xl:hidden"
          >
            <video
              autoplay
              loop
              muted
              playsinline
              class="w-full object-cover max-xl:h-175"
              poster="/images/nft/video-tablet-mobile-poster.webp"
            >
              <source
                src="/images/nft/video-tablet-mobile.mp4"
                type="video/mp4"
              />
            </video>
          </div>
          <div
            class="w-full h-full object-cover absolute top-0 -z-1 max-sm:hidden max-xl:hidden"
          >
            <video
              autoplay
              loop
              muted
              playsinline
              class="w-full h-full object-cover"
              poster="/images/nft/video-desktop-poster.png"
            >
              <source src="/images/nft/video-desktop.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </div>
    </section>

    <section>
      <div class="flex items-center justify-center mt-25.5 max-xl:mt-5">
        <table
          class="div-table border border-[#272727] [&_td]:!border-0 min-h-201"
        >
          <tbody>
            <tr v-for="(feature, index) in features" :key="index">
              <td>
                <div class="flex items-center gap-2 px-2 md:px-3.5 min-w-35">
                  <div
                    class="items-center justify-center w-full max-w-5.5 hidden lg:flex"
                  >
                    <NuxtImg
                      v-if="feature.icon"
                      :src="`/images/creator/icons/${feature.icon}.png`"
                      :class="feature.class"
                    />
                  </div>
                  <p
                    v-if="feature.titleKey"
                    class="m-0 text-xs lg:text-sm"
                    :class="{
                      'min-h-35  pt-6': feature.titleKey === 'cost',
                    }"
                  >
                    {{ t(feature.titleKey) }}
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div
          class="overflow-x-scroll scrollbar-hide border-r border-t border-b border-[#272727] [&_td]:border-r [&_td]:border-[#272727]"
        >
          <table class="[&_td]:border-0">
            <tbody>
              <tr>
                <td v-for="(h, index) in headers" :key="index" class="h-19.25">
                  <NuxtImg :src="h.src" :alt="h.alt" />
                </td>
              </tr>

              <tr v-for="(row, rowIndex) in rows" :key="rowIndex">
                <td
                  v-for="(status, colIndex) in row"
                  :key="colIndex"
                  align="center"
                >
                  <component :is="status === 'done' ? IconDone : IconNotDone" />
                </td>
              </tr>
              <tr>
                <td
                  v-for="(cell, index) in summaryRow"
                  :key="index"
                  class="min-w-66.5 max-w-66.5 px-4"
                  style="vertical-align: top"
                >
                  <div class="min-h-35 pt-6 text-sm">
                    {{ t(cell.key) }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
    <section>
      <div class="flex justify-center max-xl:flex-col max-xl:items-center">
        <img
          alt="Your Chats & Polls"
          class="w-126.75 h-127.25 object-cover mt-5.5 mb-16.5 max-xl:mt-0 max-xl:hidden"
          src="/images/creator/community-ios.webp"
        />
        <div class="community-ios xl:!hidden">
          <img
            :alt="t('your-community') + t('on-ios')"
            class="community-ios-mobile h-74 w-83.5 object-cover"
            src="/images/creator/community-ios-mobile.webp"
          />
          <div
            class="w-88.5 absolute bottom-0 left-0 right-0 mx-auto z-2 object-cover"
          >
            <img
              :alt="t('your-community') + t('on-ios')"
              class="h-20.5 w-23.5 bottom-2"
              src="/images/creator/community-ios-mobile-icon.webp"
            />
          </div>
        </div>
        <div class="mt-40.5 ml-50 max-xl:mx-5.5 max-xl:mb-10.5 max-xl:mt-4">
          <div
            class="text-[1.75rem] leading-[2.625rem] tracking-wide max-xl:text-center max-sm:text-2xl"
          >
            {{ t('your-community') }}
            <span class="text-[#CB99FF]">{{ t('on-ios') }}</span>
          </div>
          <div
            class="max-w-115 mt-10 text-[1.125rem] leading-[1.6875rem] tracking-wide text-[#CECECE] max-xl:text-center max-sm:text-lg"
          >
            {{ t('the-only-nft-launchpad') }}
          </div>
        </div>
      </div>
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <div
        class="flex flex-row justify-center max-xl:flex-col-reverse max-xl:items-center"
      >
        <div class="mt-44.5 mr-37.5 max-xl:mx-5.5 max-xl:mb-16 max-xl:mt-0">
          <div
            class="text-[1.75rem] leading-[2.625rem] tracking-wide max-xl:text-center max-sm:text-2xl"
          >
            {{ t('creator') }}
            <span class="text-[#7540EE]">{{ t('perks') }} </span>
          </div>
          <div
            class="max-w-115 mt-11.5 text-[1.125rem] leading-[1.6875rem] tracking-wide text-[#CECECE] max-xl:text-center max-sm:text-lg"
          >
            {{ t('gain-access-to-new-ways') }}
          </div>
        </div>
        <img
          :alt="t('creator') + t('perks')"
          class="w-76.75 h-62.25 mt-44.5 mb-43 max-sm:w-58.5 max-sm:h-full max-xl:mt-11 max-xl:mb-17"
          src="/images/creator/perks.webp"
        />
      </div>
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <div
        class="flex justify-center mt-20 mb-33 max-xl:flex-col max-xl:items-center max-xl:mb-17 max-xl:mt-18.5"
      >
        <div class="gate-block">
          <div class="gate-block-line" />
          <div class="gate-block-2">
            <div class="mx-7 mt-7">
              <div class="flex flex-row items-center w-full justify-between">
                <div class="font-medium text-lg tracking-wide">
                  {{ t('gate-this-hub-with-nft') }}
                </div>
                <img
                  alt="Gate this Hub with NFT switch"
                  class="w-9.5 h-6"
                  src="~/assets/images/creator/gate-switch.svg"
                />
              </div>
              <div class="mt-10 text-sm tracking-wide">
                {{ t('a-new-nft-smart-contract') }}
              </div>
            </div>
            <div class="gate-bg">
              <img
                :alt="t('gate-this-hub-with-nft')"
                class="w-full h-full"
                src="~/assets/images/creator/gate-bg.svg"
              />
            </div>
          </div>
          <div class="gate-block-line" />
        </div>

        <div class="mt-37.5 ml-32.5 max-xl:mx-5.5 max-xl:mb-10.5 max-xl:mt-4">
          <div
            class="text-[1.75rem] leading-[2.625rem] tracking-wide max-xl:text-center max-sm:text-2xl"
          >
            {{ t('your') }}
            <span class="text-[#CB99FF]">{{ t('chats-polls') }}</span>
          </div>
          <div
            class="max-w-115 mt-10 text-[1.125rem] leading-[1.6875rem] tracking-wide text-[#CECECE] max-xl:text-center max-sm:text-lg"
          >
            {{ t('the-town-square-of-your-community') }}
          </div>
        </div>
      </div>
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <div
        class="flex flex-col relative h-168 max-xl:flex-col max-xl:items-center"
      >
        <img
          :alt="t('audited-smart-contract')"
          class="w-126 h-126 mt-16.5 mx-auto object-cover -z-1 max-xl:hidden"
          src="/images/nft/image-1-mobile.webp"
        />
        <img
          :alt="t('audited-smart-contract')"
          class="audited-img w-101.5 h-101.5 mx-auto object-contain -z-1 max-xl:mb-60 max-xl:mt-6 xl:hidden"
          src="/images/nft/image-1-mobile.webp"
        />
        <div class="audited-text">
          <div
            class="text-[1.75rem] text-center leading-10.5 tracking-wide text-white max-xl:text-center max-sm:text-2xl"
          >
            {{ t('audited-smart-contract') }}
          </div>
          <div
            class="max-w-115 mt-10 text-[1.125rem] text-center mx-auto leading-6.75 tracking-wide text-[#CECECE] max-xl:text-center max-xl:mb-0 max-sm:text-lg"
          >
            {{ t('save-tens-of-thousands') }}
          </div>
        </div>
      </div>
    </section>
    <div class="w-full h-0.25 bg-gray-500" />
    <section>
      <div
        class="secondary-royalties flex justify-center max-xl:flex-col-reverse max-xl:items-center max-sm:!bg-none"
      >
        <div
          class="mt-44.5 mr-11.5 max-xl:flex-col max-xl:mx-5.5 max-xl:mb-10 max-xl:mt-0"
        >
          <div
            class="text-[1.75rem] leading-10.5 tracking-wide max-xl:text-center max-sm:text-2xl"
          >
            {{ t('receive') }}
            <span class="text-[#72FFCC]">{{ t('secondary-royalties') }}</span>
          </div>
          <div
            class="max-w-115 mt-10 text-[1.125rem] leading-6.75 tracking-wide text-[#CECECE] max-xl:text-center max-sm:text-lg"
          >
            {{ t('configure-your-membership-pass') }}
          </div>
        </div>
        <img
          :alt="t('receive') + t('secondary-royalties')"
          class="w-155.5 h-81.5 mt-34 mb-33.5 object-cover max-xl:mt-6 max-xl:mb-7.5 max-sm:w-full"
          src="/images/creator/image-2.webp"
        />
      </div>
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <div
        class="soulbound flex justify-center max-xl:flex-col-reverse max-xl:items-center"
      >
        <div
          class="mt-37.5 mr-47.5 max-xl:flex-col max-xl:mx-5.5 max-xl:mb-18.5 max-xl:mt-0"
        >
          <div
            class="max-w-110 text-[1.75rem] leading-[2.625rem] tracking-wide max-xl:text-center max-sm:text-2xl"
          >
            <span class="text-[#7540EE]">{{ t('generate-soulbound') }}</span>
            {{ t('non-transferrable') }}
            <span class="text-[#7540EE]">{{ t('nfts') }}</span>
          </div>
          <div
            class="max-w-115 mt-10 text-[1.125rem] leading-[1.6875rem] tracking-wide text-[#CECECE] max-xl:text-center max-sm:text-lg"
          >
            {{ t('make-sure-your-membership') }}
          </div>
        </div>
        <div
          class="soulbound-image w-72 max-xl:w-full max-xl:flex max-xl:justify-center"
        >
          <img
            :alt="t('generate-soulbound')"
            class="w-72 h-125.25 mb-15.5 max-xl:mb-0 max-xl:h-74 max-xl:object-cover max-xl:mb-3.5"
            src="/images/nft/image-3.webp"
          />
        </div>
      </div>
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <div
        class="founder flex justify-center max-xl:flex-col max-xl:items-center"
      >
        <img
          alt="Your Chats & Polls"
          class="w-111.75 h-144.5 object-cover mt-4 max-xl:mt-0 max-xl:hidden"
          src="/images/nft/phone-2.webp"
        />
        <div class="phone-2-mobile w-full flex justify-center xl:hidden">
          <img
            alt="Your Chats & Polls"
            class="w-73.5 h-74 max-sm:object-cover max-sm:object-top"
            src="/images/nft/phone-7-mobile.webp"
          />
        </div>
        <div class="mt-37.5 ml-32.5 max-xl:mx-5.5 max-xl:mb-10.5 max-xl:mt-4">
          <div
            class="text-[1.75rem] leading-[2.625rem] tracking-wide max-xl:text-center max-sm:text-2xl"
          >
            {{ t('your') }}
            <span class="text-[#CB99FF]">{{ t('chats-polls') }}</span>
          </div>
          <div
            class="max-w-115 mt-10 text-[1.125rem] leading-[1.6875rem] tracking-wide text-[#CECECE] max-xl:text-center max-sm:text-lg"
          >
            {{ t('the-town-square-of-your-community') }}
          </div>
        </div>
      </div>
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <NotificationsThatMatter />
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <EnrichedMemberProfiles />
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <YourPrivateEvent />
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <HolderMap />
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <div
        class="text-center font-light text-[2.625rem] leading-[2.625rem] tracking-wide mt-22.5 max-md:text-[1.75rem]"
      >
        {{ t('the-hub') }}
      </div>
      <div
        class="mt-22 max-w-173 text-[1.125rem] leading-[1.6875rem] tracking-wide text-center mx-auto max-md:text-base max-md:leading-6 max-md:px-17"
      >
        {{ t('your-pass-is-the-key') }}
      </div>
    </section>
    <NftHubs />
    <section>
      <AGrowthHack />
      <div class="w-full h-0.25 bg-gray-500" />
    </section>
    <section>
      <Supported class="pt-8 xl:pt-32.5 pb-17" />
    </section>
    <section>
      <InstallSection class="px-5" />
    </section>
  </div>
</template>

<style scoped>
@reference "tailwindcss";

.header-shadow {
  &:after {
    @apply absolute bottom-0 w-full h-29.5;
    content: '';
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
  }
}

.founder-block {
  @apply relative bg-cover bg-center;
  background: url(~/assets/images/creator/founder-bg.webp);
  &:before {
    @apply absolute top-0 w-full h-43 z-0;
    content: '';
    background: linear-gradient(180deg, #151515 0%, rgba(21, 21, 21, 0) 100%);
  }
  &:after {
    @apply absolute bottom-0 w-full h-46.5 z-2;
    content: '';
    background: linear-gradient(
      180deg,
      rgba(21, 21, 21, 0) -17.46%,
      #151515 100%
    );
  }
}

.nft-passes {
  @apply relative;
  &:before {
    @apply absolute bottom-0 w-full h-42 z-2;
    content: '';
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
  }
}

.gradient {
  background: linear-gradient(360deg, #0069ec 0%, #00b6f6 100%);
}

.gradient-install {
  background: linear-gradient(80.26deg, #000000 36.07%, #7540ee 103.92%);
}

.phone-5-mobile {
  &:after {
    @apply absolute top-0 w-full h-30 z-2;
    content: '';
    background: linear-gradient(180deg, #151515 0%, rgba(34, 34, 34, 0) 100%);
  }
}

.hub-card {
  &:nth-child(1) {
    @apply mt-0;
  }
  @apply -mt-27.5;
}

.community {
  @apply relative;
  &:after {
    @apply absolute bottom-0 w-full h-42;
    content: '';
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
  }
}

.soulbound {
  @apply relative;
  &:before {
    @apply absolute top-0 w-full h-42 z-1;
    content: '';
    background: linear-gradient(0, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
  }
}

.founder {
  @apply relative;
  &:after {
    @apply absolute bottom-0 w-full h-42;
    content: '';
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
  }
}

.secondary-royalties {
  @apply relative bg-cover bg-center z-2;
  background: url(~/assets/images/creator/secondary-royalties-bg.webp) no-repeat;
  &::before {
    @apply absolute bottom-0 w-full h-124 -z-1;
    content: '';
    background: linear-gradient(180deg, rgba(21, 21, 21, 0) 0%, #151515 68.05%);
  }
}

.web3 {
  &::before {
    @apply absolute top-0 w-full h-30 z-2;
    content: '';
    background: linear-gradient(180deg, #151515 0%, rgba(34, 34, 34, 0) 100%);
  }
  &::after {
    @apply absolute bottom-0 w-full h-30 z-2 left-0 right-0;
    content: '';
    background: linear-gradient(0deg, #151515 0%, rgba(34, 34, 34, 0) 100%);
  }
}

.member-profiles {
  &::after {
    @apply absolute bottom-0 w-full h-30 z-2 left-0 right-0;
    content: '';
    background: linear-gradient(0deg, #151515 0%, rgba(34, 34, 34, 0) 100%);
  }
}

.audited-text {
  @apply absolute left-0 right-0 flex flex-col items-center max-w-163.5 h-64.75 mx-auto top-112.5 max-xl:px-3.5 max-xl:top-auto max-xl:bottom-19.5;
}

.price-text {
  @apply text-sm tracking-wide ml-6 mt-1.5 max-xl:mt-1.75;
}

.gate-block {
  @apply flex flex-col items-center justify-center relative w-134 h-126.75;
  &::before {
    @apply w-full h-24 absolute bottom-0 left-0;
    content: '';
    background: #151515;
    filter: blur-12.5;
  }
  &::after {
    @apply w-full h-24 absolute top-0 left-0;
    content: '';
    background: #151515;
    filter: blur-12.5;
  }
}

.gate-block-2 {
  @apply border border-[#494949] rounded-sm relative w-96.5 h-49.25 my-3.75;
}

.gate-block-line {
  @apply w-96.5 h-19.75 bg-gray-500;
}

.gate-bg {
  @apply absolute top-0 -z-1;
  &::after {
    @apply w-full h-full absolute bottom-0;
    content: '';
    background: linear-gradient(
      224.29deg,
      rgba(1, 1, 1, 0) 0%,
      #7540ee 105.65%
    );
  }
}

table {
  @apply text-base tracking-wide;
  border-collapse: collapse;
}

td {
  &:not(.td-left) {
    @apply h-15;
    img {
      @apply mx-auto;
    }
  }
}

.community-ios {
  @apply relative w-full flex justify-center;
  &::after {
    @apply absolute bottom-0 w-full h-30;
    content: '';
    background: linear-gradient(0deg, #151515 0%, rgba(34, 34, 34, 0) 100%);
  }
}

@media (max-width: 1279.9px) {
  .nft-passes:before {
    @apply hidden;
  }
  .founder-block,
  .community,
  .founder {
    @apply bg-none;
    &:before,
    &:after {
      @apply hidden;
    }
  }
  .header-shadow {
    &:after {
      @apply -z-1;
    }
  }
  .soulbound {
    &:before {
      @apply h-30;
    }
    img {
      object-position: 0 -111px;
    }
    .soulbound-image {
      @apply relative;
      &:after {
        @apply absolute bottom-0 w-full h-30 z-1;
        content: '';
        background: linear-gradient(
          180deg,
          rgba(34, 34, 34, 0) 0%,
          #151515 100%
        );
      }
    }
  }
}

@media (max-width: 640px) {
  .gate-block {
    @apply w-auto h-auto;
    &::before,
    &::after,
    .gate-block-line {
      @apply hidden;
    }
  }
  .td-left {
    @apply min-w-55;
  }
}

@media (max-width: 400px) {
  .gate-block-2 {
    @apply w-76 h-full;
    div:not(.gate-bg) {
      @apply p-2 m-0;
    }
    .gate-bg {
      @apply h-full;
      img {
        @apply h-full;
      }
    }
  }
}

@media (max-width: 375px) {
  .supply-block {
    @apply flex-col items-center;
  }
  .price-text {
    @apply mx-0 mt-4;
  }
  .community-ios {
    @apply w-full h-auto;
    .community-ios-mobile {
      @apply w-[calc(100%_-_28px)] h-full;
    }
    div {
      @apply w-full pl-2;
    }
  }
}
</style>
